import { useState } from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { MeshGradient } from '../components/AnimatedBackground'
import {
  ArrowLeft,
  Mail,
  Lock,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  Check,
  X
} from 'lucide-react'
import { useAuth } from '../lib/auth'
import NexaticeIcon from '../assets/nexatice-icon.svg'

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /[0-9]/.test(p) },
  { label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) }
]

export function Register() {
  const { register, isLoading, error, clearError } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [showRequirements, setShowRequirements] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)
    clearError()

    if (!email || !password || !confirmPassword) {
      setLocalError('Please fill in all fields')
      return
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match')
      return
    }

    const allRequirementsMet = PASSWORD_REQUIREMENTS.every((req) => req.test(password))
    if (!allRequirementsMet) {
      setLocalError('Password does not meet all requirements')
      return
    }

    const result = await register(email, password)
    if (result.success) {
      window.history.pushState({}, '', '/dashboard')
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  const displayError = localError || error

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="auth" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>

          <div className="auth-card">
            <div className="auth-header">
              <img src={NexaticeIcon} alt="Nexatice" className="auth-logo" />
              <h1>Create account</h1>
              <p>Get started with Nexatice</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {displayError && (
                <div className="auth-error">
                  <AlertCircle className="w-4 h-4" />
                  {displayError}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-wrapper">
                  <Mail className="input-icon" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <Lock className="input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setShowRequirements(true)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="input-action"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {showRequirements && password && (
                  <div className="password-requirements">
                    {PASSWORD_REQUIREMENTS.map((req, i) => (
                      <div
                        key={i}
                        className={`requirement ${req.test(password) ? 'met' : 'unmet'}`}
                      >
                        {req.test(password) ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <X className="w-3 h-3" />
                        )}
                        {req.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="input-wrapper">
                  <Lock className="input-icon" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="input-action"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="input-hint error">Passwords do not match</p>
                )}
              </div>

              <p className="terms-text">
                By creating an account, you agree to our{' '}
                <a href="/terms" className="text-link">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-link">
                  Privacy Policy
                </a>
              </p>

              <button type="submit" className="auth-submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>Already have an account?</span>
            </div>

            <a href="/login" className="auth-alt-action">
              Sign in
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

